/**
 * Saved Links React Query Hooks
 *
 * Replaces useSavedLinksStore for server-side saved link data.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { graphqlFetch } from '../database/graphqlClient';
import { queryKeys } from './queryKeys';
import type { SavedLink, LinkAssociation } from '../domain/savedLink';

// =============================================================================
// GRAPHQL
// =============================================================================

const SAVED_LINK_FIELDS = `
  id
  url
  domain
  title
  description
  source {
    sourceType
    sourceId
    folder
    sourceLabel
  }
  associations {
    entityId
    entityLabel
    entityType
    confidence
    confirmed
  }
  savedAt
`;

const SAVED_LINKS_QUERY = `
  query GetSavedLinks {
    savedLinks {
      ${SAVED_LINK_FIELDS}
    }
  }
`;

const CREATE_SAVED_LINK_MUTATION = `
  mutation CreateSavedLink($input: SavedLinkInput!) {
    createSavedLink(input: $input) {
      ${SAVED_LINK_FIELDS}
    }
  }
`;

const UPDATE_SAVED_LINK_MUTATION = `
  mutation UpdateSavedLink($id: ID!, $input: SavedLinkUpdateInput!) {
    updateSavedLink(id: $id, input: $input) {
      ${SAVED_LINK_FIELDS}
    }
  }
`;

const DELETE_SAVED_LINK_MUTATION = `
  mutation DeleteSavedLink($id: ID!) {
    deleteSavedLink(id: $id)
  }
`;

// =============================================================================
// MAPPERS
// =============================================================================

function mapGraphQLSavedLink(gql: any): SavedLink {
  return {
    id: gql.id,
    url: gql.url,
    domain: gql.domain,
    title: gql.title,
    description: gql.description,
    source: {
      sourceType: gql.source.sourceType,
      sourceId: gql.source.sourceId,
      folder: gql.source.folder || undefined,
      sourceLabel: gql.source.sourceLabel,
    },
    associations: (gql.associations || []).map((a: any) => ({
      entityId: a.entityId,
      entityLabel: a.entityLabel,
      entityType: a.entityType,
      confidence: a.confidence,
      confirmed: a.confirmed,
    })),
    savedAt: gql.savedAt,
  };
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Fetch all saved links.
 */
export function useSavedLinks() {
  return useQuery({
    queryKey: queryKeys.savedLinks.list(),
    queryFn: async () => {
      const response = await graphqlFetch<{ savedLinks: any[] }>(SAVED_LINKS_QUERY);
      if (response.errors) {
        console.error('[SavedLinks] GraphQL errors:', response.errors);
      }
      return (response.data?.savedLinks || []).map(mapGraphQLSavedLink);
    },
    staleTime: 5 * 60_000,
  });
}

/**
 * Create a new saved link.
 */
export function useCreateSavedLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      url: string;
      domain: string;
      title: string;
      description: string;
      source: SavedLink['source'];
      associations: LinkAssociation[];
    }) => {
      const input = {
        url: params.url,
        domain: params.domain,
        title: params.title,
        description: params.description,
        source: {
          source_type: params.source.sourceType,
          source_id: params.source.sourceId,
          folder: params.source.folder || null,
          source_label: params.source.sourceLabel,
        },
        associations: params.associations.map((a) => ({
          entity_id: a.entityId,
          entity_label: a.entityLabel,
          entity_type: a.entityType,
          confidence: a.confidence,
          confirmed: a.confirmed,
        })),
      };

      const response = await graphqlFetch<{ createSavedLink: any }>(
        CREATE_SAVED_LINK_MUTATION,
        { input }
      );
      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }
      if (response.data?.createSavedLink) {
        return mapGraphQLSavedLink(response.data.createSavedLink);
      }
      throw new Error('Failed to create saved link');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.savedLinks.list() });
    },
  });
}

/**
 * Update an existing saved link.
 */
export function useUpdateSavedLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: {
      id: string;
      data: Partial<Pick<SavedLink, 'title' | 'description' | 'associations'>>;
    }) => {
      const input: Record<string, unknown> = {};
      if (data.title !== undefined) input.title = data.title;
      if (data.description !== undefined) input.description = data.description;
      if (data.associations !== undefined) {
        input.associations = data.associations.map((a) => ({
          entity_id: a.entityId,
          entity_label: a.entityLabel,
          entity_type: a.entityType,
          confidence: a.confidence,
          confirmed: a.confirmed,
        }));
      }

      const response = await graphqlFetch<{ updateSavedLink: any }>(
        UPDATE_SAVED_LINK_MUTATION,
        { id, input }
      );
      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }
      if (response.data?.updateSavedLink) {
        return mapGraphQLSavedLink(response.data.updateSavedLink);
      }
      throw new Error('Failed to update saved link');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.savedLinks.list() });
    },
  });
}

/**
 * Delete a saved link.
 */
export function useDeleteSavedLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await graphqlFetch<{ deleteSavedLink: boolean }>(
        DELETE_SAVED_LINK_MUTATION,
        { id }
      );
      if (response.errors) {
        throw new Error(response.errors.map(e => e.message).join(', '));
      }
      return response.data?.deleteSavedLink ?? false;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.savedLinks.list() });
    },
  });
}
